import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Reveal } from '../Reveal';
import { SEO } from '../SEO';
import { SchemaMarkup } from '../SchemaMarkup';
import { supabase } from '../../src/libs/supabase';
import { sendEmail } from '../../src/libs/sendEmail';
import { NeuralChat } from '../NeuralChat';
import {
  Check, ChevronDown, ChevronUp, MapPin, Mail, Phone,
  Linkedin, Search, FileText, Rocket, Target, Handshake, Globe,
  ArrowRight, Loader2, CheckCircle2, Plus, Minus, Code2, Heart, Shield, Zap
} from 'lucide-react';
import { Page } from '../../types';

interface AboutPageProps {
  onNavigate?: (page: Page) => void;
}

// --- SCHEMA.ORG JSON-LD ---
const SchemaMarkup = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "mainEntity": {
      "@type": "Organization",
      "name": "CleverDigital",
      "description": "KI-Agentur für Prozessautomatisierung in Wien und Österreich",
      "url": "https://cleverdigital.at/",
      "areaServed": ["Wien", "Niederösterreich", "Österreich"],
      "knowsAbout": ["Künstliche Intelligenz", "Prozessautomatisierung", "Business Automation", "KI-Beratung", "Chatbots"],
      "founders": [
        {
          "@type": "Person",
          "name": "Dmitry Pashlov"
        }
      ]
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
};

// --- FAQ COMPONENT ---
const FAQSection = () => {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      id: "FAQ_01",
      q: t('about.faq.items.q1.q'),
      a: t('about.faq.items.q1.a')
    },
    {
      id: "FAQ_02",
      q: t('about.faq.items.q2.q'),
      a: t('about.faq.items.q2.a')
    },
    {
      id: "FAQ_03",
      q: t('about.faq.items.q3.q'),
      a: t('about.faq.items.q3.a')
    },
    {
      id: "FAQ_04",
      q: t('about.faq.items.q4.q'),
      a: t('about.faq.items.q4.a')
    },
    {
      id: "FAQ_05",
      q: t('about.faq.items.q5.q'),
      a: t('about.faq.items.q5.a')
    },
    {
      id: "FAQ_06",
      q: t('about.faq.items.q6.q'),
      a: t('about.faq.items.q6.a')
    }
  ];

  return (
    <div className="flex flex-col border-t border-gray-200">
      {faqs.map((faq, index) => (
        <Reveal key={index} delay={index * 0.1}>
          <div className={`group border - b border - gray - 200 transition - colors duration - 300 ${openIndex === index ? 'bg-gray-50' : 'hover:bg-gray-50'} `}>
            <button
              className="w-full flex items-start md:items-center justify-between py-10 px-4 md:px-8 text-left"
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
            >
              <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-12">
                <span className="font-mono text-xs text-gray-400 uppercase tracking-widest">
                  // {faq.id}
                </span>
                <span className={`text - xl md: text - 2xl font - bold transition - colors duration - 300 ${openIndex === index ? 'text-clever-teal' : 'text-gray-900'} `}>
                  {faq.q}
                </span>
              </div>

              <div className="ml-4 flex-shrink-0 text-gray-400 font-mono">
                {openIndex === index ? <Minus size={20} /> : <Plus size={20} />}
              </div>
            </button>

            <div
              className={`overflow - hidden transition - all duration - 500 ease - [cubic - bezier(0.23, 1, 0.32, 1)] ${openIndex === index ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'} `}
            >
              <div className="px-4 md:px-8 pb-10 grid grid-cols-1 md:grid-cols-12">
                <div className="hidden md:block md:col-span-2"></div> {/* Spacer aligning with ID */}
                <div className="md:col-span-8">
                  <div className="text-lg leading-relaxed text-gray-600 font-light pl-4 border-l border-clever-teal/30">
                    {faq.a}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  );
};

// --- PROCESS SECTION (TIMELINE) ---
const ProcessSection = () => {
  const { t } = useTranslation();
  const steps = [
    {
      id: "01",
      title: t('about.process.steps.step1.title'),
      subtitle: t('about.process.steps.step1.subtitle'),
      desc: t('about.process.steps.step1.desc')
    },
    {
      id: "02",
      title: t('about.process.steps.step2.title'),
      subtitle: t('about.process.steps.step2.subtitle'),
      desc: t('about.process.steps.step2.desc')
    },
    {
      id: "03",
      title: t('about.process.steps.step3.title'),
      subtitle: t('about.process.steps.step3.subtitle'),
      desc: t('about.process.steps.step3.desc')
    }
  ];

  return (
    <section className="py-32 bg-[#0B1121] text-white relative overflow-hidden">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-0 -translate-y-1/2 w-[600px] h-[600px] bg-clever-teal/5 blur-[120px] pointer-events-none rounded-full"></div>

      <div className="max-w-5xl mx-auto px-6 relative z-10">
        <Reveal>
          <div className="flex flex-col md:flex-row justify-between items-end mb-32 border-b border-gray-800 pb-8">
            <h2 className="text-5xl md:text-7xl font-bold text-white tracking-tighter leading-none">
              {t('about.process.title_line1')}<br />{t('about.process.title_line2')}
            </h2>
            <div className="text-right mt-8 md:mt-0">
              <p className="text-gray-400 text-lg max-w-md">
                {t('about.process.subtitle')}
              </p>
            </div>
          </div>
        </Reveal>

        <div className="relative">
          {/* Central Line */}
          <div className="absolute left-[19px] md:left-1/2 top-0 bottom-0 w-px bg-gray-800 -translate-x-1/2"></div>

          <div className="space-y-32">
            {steps.map((step, index) => (
              <Reveal key={step.id} delay={index * 0.1}>
                <div className={`flex flex - col md: flex - row items - start md: items - center gap - 8 md: gap - 0 ${index % 2 !== 0 ? 'md:flex-row-reverse' : ''} `}>

                  {/* Content Side */}
                  <div className={`md: w - 1 / 2 ${index % 2 !== 0 ? 'md:pl-24' : 'md:pr-24'} pl - 16 w - full`}>
                    <div className="relative">
                      <span className="text-xs font-bold uppercase tracking-widest text-clever-teal mb-2 block">{step.subtitle}</span>
                      <h3 className="text-2xl font-bold text-white mb-4">{step.title}</h3>
                      <p className="text-gray-400 leading-relaxed mb-6">{step.desc}</p>
                    </div>
                  </div>

                  {/* Central Dot */}
                  <div className="absolute left-[19px] md:left-1/2 -translate-x-1/2 flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-clever-teal ring-8 ring-[#0B1121] z-10"></div>
                  </div>

                  {/* Empty Side */}
                  <div className="hidden md:block md:w-1/2" />

                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export const AboutPage: React.FC<AboutPageProps> = ({ onNavigate }) => {
  const { t, i18n } = useTranslation();
  // Form State
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    size: '',
    processes: '',
    message: '',
    dsgvo: false
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.firstName) newErrors.firstName = "Vorname ist erforderlich";
    if (!formData.lastName) newErrors.lastName = "Nachname ist erforderlich";
    if (!formData.email) {
      newErrors.email = "E-Mail ist erforderlich";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Ungültige E-Mail-Adresse";
    }
    if (!formData.phone) newErrors.phone = "Telefonnummer ist erforderlich";
    if (!formData.company) newErrors.company = "Firmenname ist erforderlich";
    if (!formData.message) newErrors.message = "Nachricht ist erforderlich";
    if (!formData.dsgvo) newErrors.dsgvo = "Zustimmung erforderlich";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      setIsSubmitting(true);

      try {
        const { error } = await supabase.from('contacts').insert([{
          name: `${formData.firstName} ${formData.lastName} `,
          company: formData.company,
          email: formData.email,
          phone: formData.phone,
          message: `Industry: ${formData.industry} \nSize: ${formData.size} \nProcesses: ${formData.processes} \nDSGVO: ${formData.dsgvo} \n\nMessage: \n${formData.message} `
        }]);

        if (error) throw error;

        sendEmail({
          email: formData.email,
          name: `${formData.firstName} ${formData.lastName} `,
          subject: 'New Analysis Request (About Page)',
          message: `New Analysis Request: \nName: ${formData.firstName} ${formData.lastName} \nCompany: ${formData.company} \nEmail: ${formData.email} \nPhone: ${formData.phone} \nIndustry: ${formData.industry} \nSize: ${formData.size} \nProcesses: ${formData.processes} \n\nMessage: \n${formData.message} `,
          lang: i18n.language
        }).catch(console.error);

        setIsSubmitting(false);
        setShowSuccess(true);
        setFormData({
          firstName: '', lastName: '', email: '', phone: '', company: '',
          industry: '', size: '', processes: '', message: '', dsgvo: false
        });
      } catch (err) {
        console.error(err);
        alert(t('common.form_error'));
        setIsSubmitting(false);
      }
    }
  };

  const scrollToForm = () => {
    document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-white min-h-screen font-sans text-gray-900 selection:bg-clever-teal selection:text-white">
      <SEO
        title={t('about.hero.title_line1') + ' ' + t('about.hero.title_line2')}
        description={t('about.hero.subtitle')}
      />
      <SchemaMarkup />

      {/* 1. HERO SECTION */}
      <section className="relative pt-40 pb-40 overflow-hidden flex flex-col justify-start items-center bg-white border-b border-gray-100">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:40px_40px] pointer-events-none"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_800px_at_50%_50%,transparent,white)] pointer-events-none"></div>

        <div className="max-w-4xl mx-auto px-6 relative z-10 w-full">
          <div className="flex flex-col items-center text-center">
            <Reveal>
              <div className="inline-flex items-center gap-3 mb-10 opacity-0 animate-[fadeIn_1s_ease-out_forwards]">
                <span className="h-px w-8 bg-gray-300"></span>
                <span className="text-xs font-mono uppercase tracking-[0.2em] text-gray-400">
                  {t('about.hero.tagline')}
                </span>
                <span className="h-px w-8 bg-gray-300"></span>
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tighter text-gray-900 mb-8 leading-[0.95]">
                {t('about.hero.title_line1')} <br />
                <span className="text-gray-900">{t('about.hero.title_line2')}</span>
              </h1>
            </Reveal>

            <Reveal delay={0.2}>
              <p className="text-xl text-gray-500 max-w-xl mx-auto mb-12 font-light leading-relaxed">
                {t('about.hero.subtitle')}
              </p>
            </Reveal>

            <Reveal delay={0.3} width="100%">
              <div className="w-full flex justify-center mt-12">
                <NeuralChat />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 2. OUR STORY & DNA (Navy Background) */}
      <section className="py-32 bg-[#0B1121] text-white border-y border-gray-800 relative overflow-hidden">
        {/* Subtle grid background */}
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)', backgroundSize: '50px 50px' }}></div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <Reveal>
            <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-gray-700 pb-8">
              <h2 className="text-5xl md:text-7xl font-bold text-white tracking-tighter leading-none">
                {t('about.dna.title_line1')}<br />{t('about.dna.title_line2')}
              </h2>
              <div className="text-right mt-8 md:mt-0">
                <p className="text-gray-400 text-lg max-w-md">
                  {t('about.dna.subtitle')}
                </p>
              </div>
            </div>
          </Reveal>

          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
            <Reveal delay={0.1}>
              <div className="space-y-6 text-lg text-gray-300 font-light leading-relaxed">
                <p>
                  <strong className="text-white font-bold">{t('about.dna.q1')}</strong> {t('about.dna.p1')}
                </p>
                <p>
                  {t('about.dna.p2')}
                </p>
                <p>
                  <strong className="text-white font-bold">{t('about.dna.q3')}</strong> {t('about.dna.p3')}
                </p>
              </div>
            </Reveal>

            <Reveal delay={0.3}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {[
                  { key: 'handcraft', icon: Code2 },
                  { key: 'sovereignty', icon: Shield },
                  { key: 'passion', icon: Heart },
                  { key: 'speed', icon: Zap }
                ].map((item) => (
                  <div key={item.key} className="bg-white/5 border border-white/10 p-6 backdrop-blur-sm hover:bg-white/10 transition-all duration-300 group">
                    <item.icon size={32} className="text-clever-teal mb-4 group-hover:scale-110 transition-transform" />
                    <h4 className="text-white font-bold mb-2">{t(`about.dna.values.${item.key}.title`)}</h4>
                    <p className="text-gray-400 text-sm">{t(`about.dna.values.${item.key}.text`)}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 3. EXPERTISE / INTRO TEXT (White Background) */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-gray-900 pb-8">
              <h2 className="text-5xl md:text-7xl font-bold text-gray-900 tracking-tighter leading-none">
                {t('about.what_we_do.title_line1')}<br />{t('about.what_we_do.title_line2')}
              </h2>
              <div className="text-right mt-8 md:mt-0">
                <p className="text-gray-500 text-lg max-w-md">
                  {t('about.what_we_do.subtitle')}
                </p>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-16">
              <div className="text-lg text-gray-600 leading-relaxed">
                <p className="mb-6">
                  {t('about.what_we_do.p1')}
                </p>
                <p>
                  {t('about.what_we_do.p2')}
                </p>
              </div>

              <div className="bg-gray-50 p-10 border-l-4 border-clever-teal h-full flex flex-col justify-center">
                <h3 className="text-sm font-bold uppercase tracking-widest text-gray-500 mb-6">{t('about.what_we_do.expertise_title')}</h3>
                <ul className="space-y-4">
                  {(t('about.what_we_do.expertise_items', { returnObjects: true }) as string[]).map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="text-clever-teal shrink-0 mt-1" size={18} />
                      <span className="text-gray-800 font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

          </Reveal>
        </div>
      </section>

      {/* 4. PROZESS BEREICH (Navy Background) */}
      <ProcessSection />

      {/* 5. TEAM BEREICH (White Background) */}
      <section className="py-32 bg-white border-y border-gray-200">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-gray-900 pb-8">
              <h2 className="text-5xl md:text-7xl font-bold text-gray-900 tracking-tighter leading-none">
                {t('about.team.title_line1')}<br />{t('about.team.title_line2')}
              </h2>
              <div className="text-right mt-8 md:mt-0">
                <p className="text-gray-500 text-lg max-w-md">
                  {t('about.team.subtitle')}
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">

              {/* Anna-Lea */}
              <div className="bg-gray-50 p-12 border border-gray-100 shadow-lg hover:shadow-xl transition-shadow flex flex-col items-center text-center group">
                <div className="w-40 h-40 bg-white rounded-full mb-8 overflow-hidden grayscale group-hover:grayscale-0 transition-all relative shadow-md border-4 border-white ring-1 ring-gray-100">
                  <img
                    src="/assets/Lili.png"
                    alt={t('about.team.members.anna.name')}
                    className="w-full h-full object-cover"
                    style={{ objectPosition: '50% 25%' }}
                  />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">{t('about.team.members.anna.name')}</h3>
                <p className="text-clever-teal font-medium text-sm uppercase tracking-widest mb-6">{t('about.team.members.anna.role')}</p>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  {t('about.team.members.anna.bio')}
                </p>
                <a href='https://www.linkedin.com/in/anna-lea-pashlova' target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[#0077b5] font-bold hover:underline transition-colors">
                  <Linkedin size={18} /> {t('about.team.linkedin')}
                </a>
              </div>

              {/* Dmitry */}
              <div className="bg-gray-50 p-12 border border-gray-100 shadow-lg hover:shadow-xl transition-shadow flex flex-col items-center text-center group">
                <div className="w-40 h-40 bg-white rounded-full mb-8 overflow-hidden grayscale group-hover:grayscale-0 transition-all relative shadow-md border-4 border-white ring-1 ring-gray-100">
                  <img src="/assets/dmitry-pashlov.jpg" alt={t('about.team.members.dmitry.name')} className="w-full h-full object-cover" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">{t('about.team.members.dmitry.name')}</h3>
                <p className="text-clever-teal font-medium text-sm uppercase tracking-widest mb-6">{t('about.team.members.dmitry.role')}</p>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  {t('about.team.members.dmitry.bio')}
                </p>
                <a href="https://www.linkedin.com/in/dmitrypashlov/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[#0077b5] font-bold hover:underline transition-colors">
                  <Linkedin size={18} /> {t('about.team.linkedin')}
                </a>
              </div>

              {/* Lisa Bauer */}
              <div className="bg-gray-50 p-12 border border-gray-100 shadow-lg hover:shadow-xl transition-shadow flex flex-col items-center text-center group">
                <div className="w-40 h-40 bg-white rounded-full mb-8 overflow-hidden grayscale group-hover:grayscale-0 transition-all relative shadow-md border-4 border-white ring-1 ring-gray-100">
                  <img src="/assets/young-business-woman-in-the-office_.jpeg" alt={t('about.team.members.lisa.name')} className="w-full h-full object-cover" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">{t('about.team.members.lisa.name')}</h3>
                <p className="text-clever-teal font-medium text-sm uppercase tracking-widest mb-6">{t('about.team.members.lisa.role')}</p>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  {t('about.team.members.lisa.bio')}
                </p>
              </div>

              {/* Technical Team */}
              <div className="bg-gray-50 p-12 border border-gray-100 shadow-lg hover:shadow-xl transition-shadow flex flex-col items-center text-center group">
                <div className="w-40 h-40 bg-white rounded-full mb-8 overflow-hidden grayscale group-hover:grayscale-0 transition-all relative shadow-md border-4 border-white ring-1 ring-gray-100">
                  <img src="/assets/2150038853.jpg" alt={t('about.team.members.tech.name')} className="w-full h-full object-cover" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">{t('about.team.members.tech.name')}</h3>
                <p className="text-clever-teal font-medium text-sm uppercase tracking-widest mb-6">{t('about.team.members.tech.role')}</p>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  {t('about.team.members.tech.bio')}
                </p>
              </div>

            </div>
          </Reveal>
        </div>
      </section>

      {/* 6. KONTAKTFORMULAR (Navy Background) */}
      <section id="contact-form" className="py-32 bg-[#0B1121] border-y border-gray-800 relative overflow-hidden">
        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <Reveal>
            <div className="text-center mb-16">
              <h2 className="text-5xl md:text-7xl font-bold text-white mb-4 tracking-tighter">{t('about.form.title')}</h2>
              <p className="text-gray-400 text-lg">{t('about.form.subtitle')}</p>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <form onSubmit={handleSubmit} className="bg-white p-8 md:p-12 shadow-2xl border border-gray-100">
              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t('about.form.first_name')} *</label>
                  <input
                    type="text"
                    className={`w - full bg - gray - 50 border p - 4 text - gray - 900 focus: outline - none focus: border - clever - teal transition - colors ${errors.firstName ? 'border-red-500' : 'border-gray-200'} `}
                    placeholder={t('about.placeholder.first_name')}
                    value={formData.firstName}
                    onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                  />
                  {errors.firstName && <span className="text-red-500 text-xs">{errors.firstName}</span>}
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t('about.form.last_name')} *</label>
                  <input
                    type="text"
                    className={`w - full bg - gray - 50 border p - 4 text - gray - 900 focus: outline - none focus: border - clever - teal transition - colors ${errors.lastName ? 'border-red-500' : 'border-gray-200'} `}
                    placeholder={t('about.placeholder.last_name')}
                    value={formData.lastName}
                    onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                  />
                  {errors.lastName && <span className="text-red-500 text-xs">{errors.lastName}</span>}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t('about.form.email')} *</label>
                  <input
                    type="email"
                    className={`w - full bg - gray - 50 border p - 4 text - gray - 900 focus: outline - none focus: border - clever - teal transition - colors ${errors.email ? 'border-red-500' : 'border-gray-200'} `}
                    placeholder={t('about.placeholder.email')}
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                  />
                  {errors.email && <span className="text-red-500 text-xs">{errors.email}</span>}
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t('about.form.phone')} *</label>
                  <input
                    type="tel"
                    className={`w - full bg - gray - 50 border p - 4 text - gray - 900 focus: outline - none focus: border - clever - teal transition - colors ${errors.phone ? 'border-red-500' : 'border-gray-200'} `}
                    placeholder={t('about.placeholder.phone')}
                    value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  />
                  {errors.phone && <span className="text-red-500 text-xs">{errors.phone}</span>}
                </div>
              </div>

              <div className="space-y-2 mb-8">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t('about.form.company')} *</label>
                <input
                  type="text"
                  className={`w - full bg - gray - 50 border p - 4 text - gray - 900 focus: outline - none focus: border - clever - teal transition - colors ${errors.company ? 'border-red-500' : 'border-gray-200'} `}
                  placeholder={t('about.placeholder.company')}
                  value={formData.company}
                  onChange={e => setFormData({ ...formData, company: e.target.value })}
                />
                {errors.company && <span className="text-red-500 text-xs">{errors.company}</span>}
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t('about.form.industry')}</label>
                  <input
                    type="text"
                    className="w-full bg-gray-50 border border-gray-200 p-4 text-gray-900 focus:outline-none focus:border-clever-teal transition-colors"
                    placeholder={t('about.placeholder.industry')}
                    value={formData.industry}
                    onChange={e => setFormData({ ...formData, industry: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t('about.form.company_size')}</label>
                  <select
                    className="w-full bg-gray-50 border border-gray-200 p-4 text-gray-900 focus:outline-none focus:border-clever-teal transition-colors appearance-none rounded-none"
                    value={formData.size}
                    onChange={e => setFormData({ ...formData, size: e.target.value })}
                  >
                    <option value="">{t('about.form.size_placeholder')}</option>
                    <option value="1-10">{t('about.form.size_options.small')}</option>
                    <option value="11-50">{t('about.form.size_options.medium')}</option>
                    <option value="51-250">{t('about.form.size_options.large')}</option>
                    <option value="250+">{t('about.form.size_options.enterprise')}</option>
                  </select>
                </div>
              </div>

              <div className="space-y-2 mb-8">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t('about.form.processes_label')}</label>
                <textarea
                  className="w-full bg-gray-50 border border-gray-200 p-4 text-gray-900 focus:outline-none focus:border-clever-teal transition-colors resize-none h-24"
                  placeholder={t('about.placeholder.processes')}
                  value={formData.processes}
                  onChange={e => setFormData({ ...formData, processes: e.target.value })}
                />
              </div>

              <div className="space-y-2 mb-8">
                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">{t('about.form.message_label')} *</label>
                <textarea
                  className={`w - full bg - gray - 50 border p - 4 text - gray - 900 focus: outline - none focus: border - clever - teal transition - colors resize - none h - 32 ${errors.message ? 'border-red-500' : 'border-gray-200'} `}
                  placeholder={t('about.placeholder.message')}
                  value={formData.message}
                  onChange={e => setFormData({ ...formData, message: e.target.value })}
                />
                {errors.message && <span className="text-red-500 text-xs">{errors.message}</span>}
              </div>

              <div className="mb-8">
                <label className="flex items-start gap-3 cursor-pointer group">
                  <input
                    type="checkbox"
                    className="mt-1 w-5 h-5 border-gray-300 text-clever-teal focus:ring-clever-teal"
                    checked={formData.dsgvo}
                    onChange={e => setFormData({ ...formData, dsgvo: e.target.checked })}
                  />
                  <span className={`text - sm text - gray - 500 group - hover: text - gray - 700 ${errors.dsgvo ? 'text-red-500' : ''} `}>
                    {t('about.form.dsgvo_text')} *
                  </span>
                </label>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-clever-teal text-white py-6 font-bold uppercase tracking-widest hover:bg-red-700 transition-all shadow-lg flex items-center justify-center gap-3 disabled:opacity-70"
              >
                {isSubmitting ? <><Loader2 className="animate-spin" /> {t('about.form.submitting')}</> : <>{t('about.form.submit')} <ArrowRight /></>}
              </button>
              <p className="text-center text-xs text-gray-400 mt-4">Protected by reCAPTCHA</p>

            </form>
          </Reveal>
        </div>
      </section>

      {/* 7. FAQ BEREICH (White Background) */}
      <section className="py-32 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <Reveal>
            <div className="flex flex-col md:flex-row justify-between items-end mb-24 border-b border-gray-900 pb-8">
              <h2 className="text-5xl md:text-7xl font-bold text-gray-900 tracking-tighter leading-none">
                {t('about.faq.title_line1')}<br />{t('about.faq.title_line2')}
              </h2>
              <div className="text-right mt-8 md:mt-0">
                <p className="text-gray-500 text-lg max-w-md">
                  {t('about.faq.subtitle')}
                </p>
              </div>
            </div>
            <FAQSection />
          </Reveal>
        </div>
      </section>

      {/* 8. VERTRAUENS BEREICH (Navy Background) */}
      <section className="py-24 bg-[#0B1121] border-t border-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal>
            <h2 className="text-2xl font-bold text-white mb-12 text-center uppercase tracking-widest text-opacity-50">{t('about.trust.title')}</h2>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                { key: 'focus', icon: <Target size={24} /> },
                { key: 'partnership', icon: <Handshake size={24} /> },
                { key: 'expertise', icon: <Globe size={24} /> }
              ].map((item, i) => (
                <div key={i} className="bg-white/5 p-8 border border-white/10 shadow-sm hover:bg-white/10 transition-all">
                  <div className="w-12 h-12 bg-clever-teal/20 text-clever-teal flex items-center justify-center rounded mb-6 border border-clever-teal/20">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{t(`about.trust.items.${item.key}.title`)}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{t(`about.trust.items.${item.key}.text`)}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* 9. BOTTOM CTA (White Background - Inverse) */}
      <section className="py-32 bg-white text-center border-t border-gray-100">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <h2 className="text-5xl md:text-7xl font-bold text-gray-900 mb-8 tracking-tighter leading-[0.9]">
              {t('about.cta.title_line1')} <br /> {t('about.cta.title_line2')}
            </h2>
            <p className="text-xl text-gray-500 mb-12">
              {t('about.cta.subtitle')}
            </p>
            <button
              onClick={scrollToForm}
              className="bg-clever-teal text-white px-12 py-6 font-bold uppercase tracking-widest text-sm hover:bg-red-700 transition-all shadow-2xl shadow-red-900/20 hover:-translate-y-1 inline-flex items-center gap-3 rounded-sm"
            >
              {t('about.cta.button')} <ArrowRight size={16} />
            </button>
          </Reveal>
        </div>
      </section>

      {/* SUCCESS MODAL */}
      {showSuccess && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-6 animate-fade-in">
          <div className="bg-white p-12 max-w-lg w-full relative shadow-2xl text-center">
            <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check size={40} />
            </div>
            <h3 className="text-2xl font-bold mb-4 text-gray-900">{t('about.form.success_title')}</h3>
            <p className="text-gray-600 mb-6 leading-relaxed">
              {t('about.form.success_msg')}
            </p>
            <div className="bg-gray-50 p-4 rounded border border-gray-200 text-sm text-gray-500 mb-8 text-left">
              <strong>💡 {t('about.form.success_tip_label')}:</strong> {t('about.form.success_tip')}
            </div>
            <button
              onClick={() => { setShowSuccess(false); if (onNavigate) onNavigate('home'); }}
              className="w-full bg-[#0B1121] text-white py-4 font-bold uppercase tracking-widest hover:bg-gray-900 transition-colors"
            >
              {t('about.form.success_button')}
            </button>
          </div>
        </div>
      )}

    </div>
  );
};
